import React from "react";
import { TableRow, TableCell, IconButton, Box, Typography, Chip } from "@mui/material";
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { TranslationKeys } from "./utils";
import { formatLicensePlate, getStatusColor, formatDate } from "./utils";
import type { Truck } from "../../../types/truck";
import type { TruckHistory } from "../../../types/truckHistory";
import type { City } from "../../../types/city";
import type { PaginationResponse } from "../../../types/paginationResponse";
import TruckRowExpanded from "./TruckRowExpanded";
import { retireTruckApi, unretireTruckApi } from "../../../api/trucksApi";
import { toastEmitter } from "../../../utils/toastEmitter";
import { getErrorMessage } from "../../../utils/errorHandling";
import type { RootState } from "../../../store";
import type { AppDispatch } from "../../../store";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import SourceChip from "../../common/SourceChip";
import { searchTrucks } from "../../../store/features/truckSlice";
import { selectCurrentPage, selectPageMetadata } from "../../../store/features/appStateSlice";
import { fetchTrucksInGroup } from "../../../store/features/trucksInGroupSlice";
import { searchRetiredTrucks } from "../../../store/features/retiredTruckSlice";
import { fetchTruckHistories } from "../../../store/features/truckHistorySlice";
import { TestId } from "../../../constants/testIds";
import { isUserAdmin } from "../../../types/user";

interface TruckRowProps {
  truck: Truck;
  isExpanded: boolean;
  onToggleRow: (truckId: string) => void;
  onAddHistory: (truckId: string) => void;
  history: TruckHistory[];
  loading: boolean;
  getCityName: (cityId: string) => string;
  pagination?: PaginationResponse;
  onTruckUpdate?: (truck: Truck) => void;
  cities: City[];
}

const TruckRow: React.FC<TruckRowProps> = ({
  truck,
  isExpanded,
  onToggleRow,
  onAddHistory,
  history,
  loading,
  getCityName,
  pagination,
  onTruckUpdate,
  cities,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.user);
  const currentPage = useSelector(selectCurrentPage);
  const pageMetadata = useSelector(selectPageMetadata);
  const isAdmin = isUserAdmin(user);
  const [retireDialogOpen, setRetireDialogOpen] = React.useState(false);
  const [retiring, setRetiring] = React.useState(false);
  const [unretireDialogOpen, setUnretireDialogOpen] = React.useState(false);
  const [unretiring, setUnretiring] = React.useState(false);
  const [shouldRefetch, setShouldRefetch] = React.useState(false);

  const currentState = truck.currentState || {};
  const location = currentState.location || {};
  const destination = (currentState.destination as { cityId?: string }) || {};
  const status = currentState.status || "UNKNOWN";
  const timestamp = currentState.timestamp || "UNKNOWN";

  // Refetch data when page changes after retiring/unretiring
  React.useEffect(() => {
    if (shouldRefetch) {
      if (currentPage === "retiredTrucks") {
        dispatch(
          searchRetiredTrucks({
            filters: {},
            pagination: {
              page: pagination?.page || 1,
              pageSize: pagination?.pageSize || 10,
            },
          }),
        );
      } else if (currentPage === "trucks") {
        dispatch(
          searchTrucks({
            filters: {},
            pagination: {
              page: pagination?.page || 1,
              pageSize: pagination?.pageSize || 10,
            },
          }),
        );
      }
      setShouldRefetch(false);
    }
  }, [currentPage, shouldRefetch, dispatch, pagination]);

  const handleRetire = async (notes?: string) => {
    setRetiring(true);
    try {
      const updatedTruck = await retireTruckApi(truck._id, notes);

      // Update the appropriate slice based on the current page
      if (currentPage === "trucks") {
        await dispatch(
          searchTrucks({
            filters: {},
            pagination: {
              page: pagination?.page || 1,
              pageSize: pagination?.pageSize || 10,
            },
          }),
        ).unwrap();
      } else if (currentPage === "truckGroup" && pageMetadata.id) {
        await dispatch(
          fetchTrucksInGroup({
            groupId: pageMetadata.id,
            page: pagination?.page || 1,
            pageSize: pagination?.pageSize || 10,
          }),
        ).unwrap();
      }

      // Fetch updated history
      await dispatch(
        fetchTruckHistories({
          truckId: truck._id,
          page: 1,
          pageSize: 10,
        }),
      ).unwrap();

      if (onTruckUpdate) {
        onTruckUpdate(updatedTruck);
      }

      toastEmitter.emit(t("Truck retired successfully" as TranslationKeys), "success");
      setRetireDialogOpen(false);
      setShouldRefetch(true);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "retireTruck");
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setRetiring(false);
    }
  };

  const handleUnretire = async (notes?: string, cityId?: string) => {
    setUnretiring(true);
    try {
      const updatedTruck = await unretireTruckApi(truck._id, notes, cityId);

      // Update the appropriate slice based on the current page
      if (currentPage === "trucks") {
        await dispatch(
          searchTrucks({
            filters: {},
            pagination: {
              page: pagination?.page || 1,
              pageSize: pagination?.pageSize || 10,
            },
          }),
        ).unwrap();
      } else if (currentPage === "truckGroup" && pageMetadata.id) {
        await dispatch(
          fetchTrucksInGroup({
            groupId: pageMetadata.id,
            page: pagination?.page || 1,
            pageSize: pagination?.pageSize || 10,
          }),
        ).unwrap();
      } else if (currentPage === "retiredTrucks") {
        await dispatch(
          searchRetiredTrucks({
            filters: {},
            pagination: {
              page: pagination?.page || 1,
              pageSize: pagination?.pageSize || 10,
            },
          }),
        ).unwrap();
      }

      // Fetch updated history
      await dispatch(
        fetchTruckHistories({
          truckId: truck._id,
          page: 1,
          pageSize: 10,
        }),
      ).unwrap();

      if (onTruckUpdate) {
        onTruckUpdate(updatedTruck);
      }

      toastEmitter.emit(t("Truck unretired successfully" as TranslationKeys), "success");
      setUnretireDialogOpen(false);
      setShouldRefetch(true);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "unretireTruck");
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setUnretiring(false);
    }
  };

  return (
    <>
      <TableRow
        sx={{
          ...(isExpanded && {
            backgroundColor: (theme) => `${theme.palette.action.hover} !important`,
            "& .MuiTableCell-root": {
              backgroundColor: "inherit !important",
            },
          }),
          cursor: "pointer",
        }}
        onClick={() => onToggleRow(truck._id)}
        data-testid={TestId.TRUCK.ROW}
      >
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" onClick={() => onToggleRow(truck._id)}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "48px",
            }}
          >
            <Typography
              variant="subtitle1"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${TestId.TRUCK.ROW}-${truck._id}-license-plate`}
            >
              {formatLicensePlate(truck.licensePlate)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              {t(truck.type as TranslationKeys)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ width: "120px" }}>
          <Chip
            label={t(status as TranslationKeys)}
            color={getStatusColor(status) as any}
            size="small"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${TestId.TRUCK.ROW}-${truck._id}-status`}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${TestId.TRUCK.ROW}-${truck._id}-location`}
            >
              {location.cityId ? getCityName(location.cityId) : "-"}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${TestId.TRUCK.ROW}-${truck._id}-destination`}
            >
              {destination.cityId ? getCityName(destination.cityId) : "-"}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography
            onClick={(e) => e.stopPropagation()}
            data-testid={`${TestId.TRUCK.ROW}-${truck._id}-timestamp`}
          >
            {formatDate(timestamp, i18n.language)}
          </Typography>
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <SourceChip source={truck.initiallyCreatedFrom} />
        </TableCell>
        {isAdmin && (
          <TableCell onClick={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                truck.retired ? setUnretireDialogOpen(true) : setRetireDialogOpen(true);
              }}
              sx={{
                opacity: 0.7,
                padding: "2px",
                color: truck.retired ? "success.main" : "error.main",
                "&:hover": {
                  opacity: 1,
                  backgroundColor: truck.retired ? "success.light" : "error.light",
                  color: truck.retired ? "success.contrastText" : "error.contrastText",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1rem",
                },
              }}
            >
              {truck.retired ? <RestoreIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      <TruckRowExpanded
        truck={truck}
        isExpanded={isExpanded}
        history={history}
        loading={loading}
        getCityName={getCityName}
        onAddHistory={onAddHistory}
        pagination={pagination}
      />
      <ConfirmationDialog
        open={retireDialogOpen}
        onClose={() => setRetireDialogOpen(false)}
        onConfirm={handleRetire}
        title={t("Retire Truck" as TranslationKeys)}
        message={t("Are you sure you want to retire this truck?" as TranslationKeys)}
        confirmButtonText={t("Retire" as TranslationKeys)}
        loading={retiring}
        showNotes={true}
      />
      <ConfirmationDialog
        open={unretireDialogOpen}
        onClose={() => setUnretireDialogOpen(false)}
        onConfirm={handleUnretire}
        title={t("Unretire Truck" as TranslationKeys)}
        message={t("Are you sure you want to unretire this truck?" as TranslationKeys)}
        confirmButtonText={t("Unretire" as TranslationKeys)}
        loading={unretiring}
        showNotes={true}
        showCitySelection={true}
        cities={cities}
      />
    </>
  );
};

export default TruckRow;
